import { FetchError } from "ofetch";
import { isErrorResponse } from "~~/shared/api/errors";
export default function useMutation<Data, Params extends unknown[]>(opts: {
  mutationFn: (...args: Params) => Promise<Data>;
  onSuccess?: (data: Data) => void | Promise<void>;
  onError?: (error: unknown) => void;
}) {
  const status = ref<"idle" | "pending" | "success" | "error">("idle");
  const error = ref<unknown>();
  const data = ref<Data>();
  const promise = ref<Promise<Data>>();

  async function mutate(...args: Params) {
    status.value = "pending";
    try {
      promise.value = opts.mutationFn(...args);
      const results = await promise.value;
      status.value = "success";
      await opts.onSuccess?.(results);
      error.value = undefined;
      data.value = results;
    } catch (e) {
      data.value = undefined;
      if (e instanceof FetchError && isErrorResponse(e.data)) {
        error.value = e.data.message;
      } else {
        error.value = e;
      }
      status.value = "error";
      opts.onError?.(e);
    }
  }

  function reset() {
    status.value = "idle";
    error.value = undefined;
    data.value = undefined;
  }

  return {
    status,
    error,
    mutate,
    reset,
    data,
    promise: computed(
      () => promise.value || Promise.resolve<Data | undefined>(data.value),
    ),
    pending: computed(() => status.value == "pending"),
  };
}
